import { useCallback, useRef, useState } from "react";
import { useDependency } from "../../../../contexts/DependencyProvider";
import {
  Col,
  ListFPARequest,
  Order,
} from "../../../../services/soap/project/requests/ListFPARequest";
import { ListContextRequest } from "../../../../services/soap/project/requests/ListContextRequest";
import ConstantUtils from "../../../../utils/ConstantUtils";
import {
  GroupedSearchResultItemData,
  mergeGroupedSearchResults,
  processHistoryData,
  processSearchData,
} from "../../../../utils/SearchUtils";
import type { PathSegmentContextRef } from "../../../../infra/protected/Search/SearchResultData";
import type { ContextSearchScope } from "./types";

const DEBOUNCE_MS = 500;

export interface UseContextSearchStateParams {
  folderStatusMap: Record<string, unknown>;
  projectStatusMap: Record<string, unknown>;
  activityStatusMap: Record<string, unknown>;
}

export function useContextSearchState({
  folderStatusMap,
  projectStatusMap,
  activityStatusMap,
}: UseContextSearchStateParams) {
  const { projectService, store } = useDependency();
  const [searchTitle, setSearchTitle] = useState("History");
  const [searchResults, setSearchResults] = useState<GroupedSearchResultItemData[]>([]);

  const resultOffsetRef = useRef<number>(0);
  const resultRef = useRef<GroupedSearchResultItemData[]>([]);
  const searchRef = useRef<string>("");
  const resultEndRef = useRef<boolean>(false);
  const cancelSource = useRef<{ cancel: () => void } | null>(null);
  const totalCountRef = useRef<number>(0);
  const debounceTimerRef = useRef<number | null>(null);
  /** When set, ListFPA requests are limited to children of this parent and optional item type. */
  const scopeRef = useRef<ContextSearchScope>({ parent: null });

  const filterByTargetItemType = useCallback(
    (
      groups: GroupedSearchResultItemData[],
      targetItemType: PathSegmentContextRef["type"] | undefined
    ): GroupedSearchResultItemData[] => {
      if (!targetItemType) {
        return groups;
      }
      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => (item.itemType ?? "").toUpperCase() === targetItemType
          ),
        }))
        .filter((group) => group.items.length > 0);
    },
    []
  );

  const historyCallBack = useCallback(async () => {
    try {
      if (cancelSource.current) {
        cancelSource.current.cancel();
        cancelSource.current = null;
      }
      if (resultEndRef.current) return;
      const request = new ListContextRequest(
        store.Server,
        store.SessionId,
        resultOffsetRef.current,
        ConstantUtils.LIST_CONTEXT_PAGE_SIZE
      );
      cancelSource.current = request.cancelSource;

      const result = await projectService.listContext(request);
      const processed_result = processHistoryData(
        result.CONTEXTS,
        folderStatusMap,
        projectStatusMap,
        activityStatusMap
      );
      const total_items = mergeGroupedSearchResults(resultRef.current, processed_result);

      totalCountRef.current += +result.count;
      setSearchTitle(`History (${totalCountRef.current})`);
      setSearchResults(total_items);
      resultRef.current = total_items;
      resultOffsetRef.current += ConstantUtils.LIST_CONTEXT_PAGE_SIZE;
      resultEndRef.current = result.CONTEXTS.length < ConstantUtils.LIST_CONTEXT_PAGE_SIZE;
    } catch (error) {
      console.log(error);
    }
  }, [projectService, store.Server, store.SessionId, folderStatusMap, projectStatusMap, activityStatusMap]);

  const scopedBrowseCallBack = useCallback(async () => {
      try {
        if (!scopeRef.current.parent) {
          return;
        }
        if (resultEndRef.current) {
          return;
        }

        if (cancelSource.current) {
          cancelSource.current.cancel();
          cancelSource.current = null;
        }

        const request = new ListFPARequest(
          store.Server,
          store.SessionId,
          undefined,
          true,
          ConstantUtils.LIST_FPA_PAGE_SIZE,
          resultOffsetRef.current,
          undefined,
          false,
          undefined,
          scopeRef.current.parent,
          undefined,
          undefined,
          undefined,
          new Order([new Col("itemTypeName"), new Col("name"), new Col("created", true)])
        );
        cancelSource.current = request.cancelSource;

        const result = await projectService.listFPA(request);
        const tmpSearchData = processSearchData(
          result.ITEMS,
          folderStatusMap,
          projectStatusMap,
          activityStatusMap
        );
        const scopedSearchData = filterByTargetItemType(
          tmpSearchData,
          scopeRef.current.targetItemType
        );
        const processed_result = mergeGroupedSearchResults(
          resultRef.current,
          scopedSearchData
        );

        totalCountRef.current += +result.count;
        setSearchTitle(`Browse (${totalCountRef.current})`);
        setSearchResults(processed_result);

        resultRef.current = processed_result;
        resultOffsetRef.current += ConstantUtils.LIST_FPA_PAGE_SIZE;
        resultEndRef.current = +result.count < ConstantUtils.LIST_FPA_PAGE_SIZE;
      } catch (error) {
        console.log(error);
      }
  }, [projectService, store.Server, store.SessionId, folderStatusMap, projectStatusMap, activityStatusMap, filterByTargetItemType]);

  const searchCallBack = useCallback(
    async (text: string, loadMore: boolean = false) => {
      try {
        if (text !== searchRef.current) {
          resultOffsetRef.current = 0;
          resultRef.current = [];
          resultEndRef.current = false;
          totalCountRef.current = 0;
        }

        if (resultEndRef.current) {
          return;
        }

        searchRef.current = text;

        if (cancelSource.current) {
          cancelSource.current.cancel();
          cancelSource.current = null;
        }

        if (text.length < 1) {
          if (scopeRef.current.parent) {
            await scopedBrowseCallBack();
          } else {
            await historyCallBack();
          }
          return;
        }

        const request = new ListFPARequest(
          store.Server,
          store.SessionId,
          text + "*",
          true,
          ConstantUtils.LIST_FPA_PAGE_SIZE,
          resultOffsetRef.current,
          undefined,
          false,
          undefined,
          scopeRef.current.parent ?? undefined,
          undefined,
          undefined,
          undefined,
          new Order([new Col("itemTypeName"), new Col("name"), new Col("created", true)])
        );
        cancelSource.current = request.cancelSource;

        const result = await projectService.listFPA(request);
        const tmpSearchData = processSearchData(
          result.ITEMS,
          folderStatusMap,
          projectStatusMap,
          activityStatusMap
        );
        const scopedSearchData = filterByTargetItemType(
          tmpSearchData,
          scopeRef.current.targetItemType
        );
        const processed_result = mergeGroupedSearchResults(
          resultRef.current,
          scopedSearchData
        );

        totalCountRef.current += +result.count;
        setSearchTitle(`Results (${totalCountRef.current})`);
        setSearchResults(processed_result);

        resultRef.current = processed_result;
        resultOffsetRef.current += ConstantUtils.LIST_FPA_PAGE_SIZE;
        resultEndRef.current = +result.count < ConstantUtils.LIST_FPA_PAGE_SIZE;
      } catch (error) {
        console.log(error);
      }
    },
    [
      projectService,
      store.Server,
      store.SessionId,
      folderStatusMap,
      projectStatusMap,
      activityStatusMap,
      historyCallBack,
      scopedBrowseCallBack,
      filterByTargetItemType,
    ]
  );

  /** Reset pagination and load first page: global history, or scoped browse under `scope.parent`. */
  const resetAndLoadInitial = useCallback(
    (scope: ContextSearchScope) => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (cancelSource.current) {
        cancelSource.current.cancel();
        cancelSource.current = null;
      }
      resultOffsetRef.current = 0;
      resultRef.current = [];
      resultEndRef.current = false;
      totalCountRef.current = 0;
      searchRef.current = "";
      scopeRef.current = scope;
      setSearchResults([]);
      if (scope.parent) {
        void scopedBrowseCallBack();
      } else {
        void historyCallBack();
      }
    },
    [historyCallBack, scopedBrowseCallBack]
  );

  const handleSearchChangeDebounced = useCallback(
    (text: string) => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = window.setTimeout(() => {
        debounceTimerRef.current = null;
        void searchCallBack(text, false);
      }, DEBOUNCE_MS);
    },
    [searchCallBack]
  );

  const loadMore = useCallback(() => {
    if (totalCountRef.current >= ConstantUtils.LIST_FPA_PAGE_SIZE) {
      if (searchTitle.startsWith("History")) {
        void historyCallBack();
      } else if (searchRef.current.length >= 1) {
        void searchCallBack(searchRef.current, true);
      } else if (scopeRef.current.parent) {
        void scopedBrowseCallBack();
      }
    }
  }, [historyCallBack, searchCallBack, searchTitle, scopedBrowseCallBack]);

  return {
    searchTitle,
    searchResults,
    handleSearchChangeDebounced,
    resetAndLoadInitial,
    loadMore,
    searchRef,
  };
}
